/**
 * `billAttachments.*` — files attached to a vendor bill.
 *
 * Two-phase upload pattern (same shape as `identity.requestAvatarUpload` /
 * `identity.confirmAvatar`):
 *
 *   1. `requestUpload` — server validates the bill + size + content-type and
 *      returns `{ uploadUrl, objectKey }`. No DB row yet.
 *   2. SPA PUTs the binary to `uploadUrl`.
 *   3. `confirm` — server inserts a `bill_attachments` row and audits.
 *
 * `list` returns active rows for a bill (newest first). `remove` is a soft
 * delete — flips `status='removed'`, audited.
 *
 * Mirror of `identity.requestAvatarUpload` and `identity.confirmAvatar`.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/** MIME allow-list — keep in sync with `ALLOWED_ATTACHMENT_TYPES` in domain. */
export const attachmentMime = z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
export type AttachmentMime = z.infer<typeof attachmentMime>;

/** 25 MB — keep in sync with `ATTACHMENT_MAX_BYTES` in domain. */
const MAX_BYTES = 25 * 1024 * 1024;

// ── requestUpload ─────────────────────────────────────────────────
export const requestUploadInput = z.object({
  billId: uuid,
  originalName: z.string().trim().min(1, "Filename is required").max(255, "Filename is too long"),
  contentType: attachmentMime,
  sizeBytes: z
    .number()
    .int()
    .min(1, "File is empty")
    .max(MAX_BYTES, `Max ${MAX_BYTES} bytes (~25 MB)`),
});

export const requestUploadOutput = z.object({
  /** Pre-signed PUT URL. Expires in 5 minutes. */
  uploadUrl: z.string().url(),
  /** Logical key the SPA passes back to `confirm` after PUT. */
  objectKey: z.string(),
  contentType: attachmentMime,
  maxBytes: z.number().int(),
});

// ── confirm ───────────────────────────────────────────────────────
export const confirmInput = z.object({
  billId: uuid,
  objectKey: z.string().min(1),
  originalName: z.string().trim().min(1).max(255),
  contentType: attachmentMime,
  sizeBytes: z.number().int().min(1).max(MAX_BYTES),
});

export const confirmOutput = z.object({
  id: uuid,
  storageKey: z.string(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  billId: uuid,
});

export const listRow = z.object({
  id: uuid,
  billId: uuid,
  originalName: z.string(),
  storageKey: z.string(),
  contentType: z.string(),
  /** Stringified bigint so JSON round-trips don't lose precision. */
  sizeBytes: z.string(),
  status: z.enum(["attached", "removed"]),
  uploadedAt: z.string().datetime(),
  /** Public download/view URL when one can be derived, else `null`. */
  publicUrl: z.string().url().nullable(),
});

export const listOutput = z.object({
  items: z.array(listRow),
});

// ── remove ────────────────────────────────────────────────────────
export const removeInput = z.object({
  id: uuid,
});

export const removeOutput = z.object({
  id: uuid,
  removedAt: z.string().datetime(),
});

// ── derived TS types ──────────────────────────────────────────────
export type RequestUploadInput = z.infer<typeof requestUploadInput>;
export type RequestUploadOutput = z.infer<typeof requestUploadOutput>;
export type ConfirmInput = z.infer<typeof confirmInput>;
export type ConfirmOutput = z.infer<typeof confirmOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type RemoveInput = z.infer<typeof removeInput>;
export type RemoveOutput = z.infer<typeof removeOutput>;
